import { NavigationGuard } from 'vue-router'
import store from './store'

// The query parameter a guarded route says where it wanted to go. Named rather
// than spread into the sign-in route's query wholesale, so that adding a
// parameter to the sign-in form does not silently become part of a redirect, and
// so that the sign-in module can find it without knowing anything else about the
// guard.
export const REDIRECT_QUERY = 'redirect'

// Where a guard sends a visitor it is turning away, remembering the route they
// asked for so signing in can put them back on it.
//
// `fullPath` rather than `path` because a route asked for with a query or a hash
// asked for that query or hash, and dropping them would be a small lie about
// where they were going. It is deliberately a path and not a URL: nothing here
// can send a visitor off this origin, because `signIn` resolves what it is given
// against the routes in `router.ts` and ignores anything that matches none of
// them. A `redirect` in the address bar is a visitor's to write, so it is not
// trusted as a destination until something has resolved it.
function signingInAt (to: { fullPath: string }) {
  return { name: 'signin', query: { [REDIRECT_QUERY]: to.fullPath } }
}

// For a route that reads private data. A visitor with no session is sent to the
// sign-in screen, which is the only way for them to get one.
//
// This is a courtesy and not the control, and it is worth being exact about
// which: what the route would return is decided by `Metalist.AuthPlug` on every
// request, and a guard that were bypassed would produce an error rather than a
// disclosure. It exists so that a visitor without a session gets asked for one
// rather than shown a broken page, and so that the sign-in screen knows where to
// put them afterwards.
export const requiresSession: NavigationGuard = (to, _from, next) => {
  next(store.getters.signedIn ? undefined : signingInAt(to))
}

// For the administrative route, on top of a session. An account that is not an
// admin is sent to the home page rather than to the sign-in screen: asking
// somebody to sign in again would not help, since they would sign in as the same
// account and be refused again.
//
// The admin flag in the store is what the sign-in response said, which is as
// fresh as the session it arrived with and as trustworthy as the token that
// authenticated it -- and no more than that. The server re-checks the flag on
// every request under this namespace and answers `403` regardless of what the
// browser believes, so a tampered local storage entry or a stale flag gets a
// screen that renders nothing useful rather than access. The check exists so
// that a non-admin is not shown a screen whose every call will fail; treating it
// as the thing that decides is the error this comment exists to prevent.
//
// Also deliberately not reading the admin flag off the token, which does not
// carry one: `Metalist.AuthToken` keeps `is_admin` out of the claims, so there is
// nothing on the token to read and a promotion takes effect at the next sign-in
// rather than on a token that would otherwise go on claiming otherwise for its
// lifetime.
export const requiresAdmin: NavigationGuard = (to, _from, next) => {
  if (!store.getters.signedIn) {
    next(signingInAt(to))
    return
  }

  next(store.getters.admin ? undefined : { name: 'home' })
}
